import {
  Box,
  type BoxProps,
  Container,
  Grid,
  type GridProps,
  Link,
  Skeleton,
  SvgIcon,
  Typography
} from '@mui/material'
import Image from 'next/image'
import NextLink from 'next/link'

import { WPImage, WPLink } from '@/types'

export type CardsCard = {
  title: string
  subtitle?: string
  location: any
  accommodations: any
  image: WPImage
  link: WPLink
}

/**
 * Card
 */
export type CardsCardProps = GridProps & Partial<CardsCard & { skeleton: boolean }>

const CardsCard = ({
  location,
  accommodations,
  image,
  link,
  skeleton,
  sx,
  ...props
}: CardsCardProps) => {
  return (
    <Grid
      sx={{
        display: 'grid',
        position: 'relative',
        boxShadow: `0 0.4rem 1.6rem 0 rgba(0, 0, 0, 0.08)`,
        borderRadius: (theme) => theme.shape.borderRadius4,
        overflow: 'hidden',
        ...sx
      }}
      {...props}
    >
      <Box
        sx={{
          position: 'relative',
          aspectRatio: 320 / 200,
          overflow: 'hidden',
          '& img': {
            objectFit: 'cover'
          }
        }}
      >
        {image && image.url ? (
          skeleton ? (
            <Skeleton variant="rectangular" width="100%" height="100%" />
          ) : (
            <Image src={image.url} alt={image.alt || ''} fill />
          )
        ) : null}
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 2,
          gap: 2
        }}
      >
        <Box sx={{ display: 'grid', flexGrow: 1, gap: 1 }}>
          {location ? (
            <Typography variant="h6">
              {skeleton ? (
                <Skeleton width="70%" />
              ) : link ? (
                <Link
                  href={link.url}
                  component={NextLink}
                  color="text.secondaryContrastText"
                  scroll={false}
                  sx={{
                    textDecoration: 'none',
                    '&:after': {
                      content: '""',
                      position: 'absolute',
                      width: '100%',
                      height: '100%',
                      top: 0,
                      left: 0
                    }
                  }}
                >
                  {location || link.title}
                </Link>
              ) : (
                location
              )}
            </Typography>
          ) : null}

          {accommodations ? (
            <Typography variant="textRegular" color="text.secondary">
              {skeleton ? <Skeleton width="30%" /> : accommodations}
            </Typography>
          ) : null}
        </Box>

        {!skeleton ? (
          <Box sx={{ display: 'flex' }} color="text.secondary">
            <SvgIcon fontSize="small">
              <use href="#ico-arrow-next" />
            </SvgIcon>
          </Box>
        ) : null}
      </Box>
    </Grid>
  )
}

/**
 * Grid
 */
export type CardsGridProps = BoxProps &
  Partial<{
    items: CardsCard[]
  }>

const Cards = ({ items, ...props }: CardsGridProps) => {
  return (
    <Box {...props}>
      <Container>
        <Grid container>
          {items?.map((item, index) => (
            <CardsCard
              key={`item-${index}`}
              size={{ xs: 4, sm: 4, md: 3 }}
              skeleton={false}
              location={item.title}
              accommodations={item.subtitle}
              image={item.image}
              link={item.link}
            />
          ))}
        </Grid>
      </Container>
    </Box>
  )
}

export default Cards
